import {
  DAYS_TO_MILLIS,
  endOfDay,
  MINUTES_TO_MILLIS,
  startOfDay,
  subDays,
  subMonths,
  subWeeks,
  weekEnd,
  weekStart,
} from "lopermut"
import { z } from "zod"
import { Errors } from "../errors"
import { DateRange } from "../shared/common"

export const TimeFrameEnum = {
  Today: "Today",
  ThisWeek: "ThisWeek",
  Last3Days: "Last3Days",
  Last1Week: "Last1Week",
  Last2Weeks: "Last2Weeks",
  Last30Days: "Last30Days",
  LastMonth: "LastMonth",
  Last3Months: "Last3Months",
  Last6Months: "Last6Months",
  Last12Months: "Last12Months",
} as const

export const MAX_TIME_FRAME_SPAN = 400 * DAYS_TO_MILLIS

const FUTURE_SLACK = DAYS_TO_MILLIS

export const TimeFramePresetSchema = z.enum(TimeFrameEnum)

export const CustomTimeFrameSchema = z
  .object({ from: z.coerce.number().int(), to: z.coerce.number().int() })
  .refine(({ from, to }) => from < to, { message: Errors.DATE_RANGE_INVALID })
  .refine(({ to }) => to <= Date.now() + FUTURE_SLACK, { message: Errors.DATE_IN_FUTURE })
  .refine(({ from, to }) => to - from <= MAX_TIME_FRAME_SPAN, {
    message: Errors.DATE_RANGE_TOO_LONG,
  })

export const TimeFrameSchema = z.union([TimeFramePresetSchema, CustomTimeFrameSchema])

export type TimeFramePreset = z.infer<typeof TimeFramePresetSchema>
export type TimeFrame = z.infer<typeof TimeFrameSchema>

export const getTimeRange = (timeFrame: TimeFrame): DateRange => {
  if (typeof timeFrame !== "string") {
    return { from: new Date(timeFrame.from), to: new Date(timeFrame.to) }
  }

  const currentDate = endOfDay(new Date())

  switch (timeFrame) {
    case TimeFrameEnum.Today:
      return {
        from: startOfDay(currentDate),
        to: currentDate,
      }
    case TimeFrameEnum.ThisWeek:
      return {
        from: weekStart(currentDate),
        to: weekEnd(currentDate),
      }
    case TimeFrameEnum.Last3Days:
      return {
        from: startOfDay(subDays(currentDate, 2)),
        to: currentDate,
      }
    case TimeFrameEnum.Last1Week:
      return {
        from: weekStart(subWeeks(currentDate, 1)),
        to: weekEnd(currentDate),
      }
    case TimeFrameEnum.Last2Weeks:
      return {
        from: weekStart(subWeeks(currentDate, 2)),
        to: weekEnd(currentDate),
      }
    case TimeFrameEnum.Last30Days:
      return {
        from: startOfDay(subDays(currentDate, 29)),
        to: currentDate,
      }
    case TimeFrameEnum.LastMonth:
      return {
        from: weekStart(subMonths(currentDate, 1)),
        to: weekEnd(currentDate),
      }
    case TimeFrameEnum.Last3Months:
      return {
        from: weekStart(subMonths(currentDate, 3)),
        to: weekEnd(currentDate),
      }
    case TimeFrameEnum.Last6Months:
      return {
        from: weekStart(subMonths(currentDate, 6)),
        to: weekEnd(currentDate),
      }
    case TimeFrameEnum.Last12Months:
      return {
        from: weekStart(subMonths(currentDate, 12)),
        to: weekEnd(currentDate),
      }
  }
}

export const TimeDimensionEnum = {
  TenMinutes: "10m",
  ThirtyMinutes: "30m",
  Hour: "1h",
  ThreeHours: "3h",
  TwelveHours: "12h",
  Day: "1d",
  ThreeDays: "3d",
  Week: "1w",
  Month: "1mo",
} as const

export const TimeDimensionSchema = z.enum(TimeDimensionEnum)

export type TimeDimension = z.infer<typeof TimeDimensionSchema>

export const DAILY_TIME_DIMENSIONS = [
  TimeDimensionEnum.Day,
  TimeDimensionEnum.ThreeDays,
  TimeDimensionEnum.Week,
  TimeDimensionEnum.Month,
] satisfies TimeDimension[]

export const MIN_BUCKETS = 6
export const MAX_BUCKETS = 180
export const TARGET_BUCKETS = 40

const TIME_DIMENSION_MINUTES = {
  [TimeDimensionEnum.TenMinutes]: 10,
  [TimeDimensionEnum.ThirtyMinutes]: 30,
  [TimeDimensionEnum.Hour]: 60,
  [TimeDimensionEnum.ThreeHours]: 3 * 60,
  [TimeDimensionEnum.TwelveHours]: 12 * 60,
  [TimeDimensionEnum.Day]: 24 * 60,
  [TimeDimensionEnum.ThreeDays]: 3 * 24 * 60,
  [TimeDimensionEnum.Week]: 7 * 24 * 60,
  [TimeDimensionEnum.Month]: 30 * 24 * 60,
} as const satisfies Record<TimeDimension, number>

const TIME_DIMENSIONS_FINEST_FIRST = Object.values(TimeDimensionEnum).sort(
  (left, right) => TIME_DIMENSION_MINUTES[left] - TIME_DIMENSION_MINUTES[right],
)

export const getTimeDimensionMinutes = (dimension: TimeDimension): number => {
  return TIME_DIMENSION_MINUTES[dimension]
}

export const getTimeDimensionOptions = (
  range: DateRange,
  allowed: readonly TimeDimension[] = TIME_DIMENSIONS_FINEST_FIRST,
) => {
  const options = allowed.filter((dimension) => {
    const buckets = getBucketCount(range, dimension)

    return buckets >= MIN_BUCKETS && buckets <= MAX_BUCKETS
  })

  if (options.length > 0) return options

  return [getClosestToTargetBuckets(range, allowed)]
}

export const getDefaultTimeDimension = (range: DateRange) => {
  return getClosestToTargetBuckets(range, getTimeDimensionOptions(range))
}

export const resolveTimeDimension = (
  range: DateRange,
  current?: TimeDimension,
  allowed?: readonly TimeDimension[],
) => {
  const options = getTimeDimensionOptions(range, allowed)

  if (current && options.includes(current)) return current

  return getClosestToTargetBuckets(range, options)
}

const getClosestToTargetBuckets = (range: DateRange, dimensions: readonly TimeDimension[]) => {
  return dimensions.reduce((closest, dimension) => {
    const distance = Math.abs(getBucketCount(range, dimension) - TARGET_BUCKETS)
    const closestDistance = Math.abs(getBucketCount(range, closest) - TARGET_BUCKETS)

    return distance < closestDistance ? dimension : closest
  })
}

const getBucketCount = (range: DateRange, dimension: TimeDimension) => {
  const spanInMillis = range.to.getTime() - range.from.getTime()

  return spanInMillis / (TIME_DIMENSION_MINUTES[dimension] * MINUTES_TO_MILLIS)
}
